import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { Plus, Edit, Trash2, X, FileText } from 'lucide-react';
import config from '../config/config';

const VoteheadManagement = () => {
   const { mode } = useParams();

  const [voteheads, setVoteheads] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    id: null,
    name: '',
    price: '',
  });
  const [loading, setLoading] = useState(false); 
  const [errors, setErrors] = useState({});
  const token = localStorage.getItem('token');

  // Fetch voteheads
  const fetchVoteheads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.API_URL}/api/pullvoteheads/${mode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      //console.log('Voteheads API response:', response.data);
      setVoteheads(response.data.data || []);
    } catch (err) {
      toast.error('Failed to fetch voteheads');
      console.error('Fetch voteheads error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Utility function to get cookie
  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    if (match) return decodeURIComponent(match[2]);
    return null;
  };

  // Save votehead
  const saveVotehead = async () => {
    setErrors({});
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        item_mode:mode,
        price: Number(form.price),
      };
       const token = localStorage.getItem('token');
      //console.log('Votehead payload:', payload);
       ////console.log('Fetching CSRF cookie...');
      const csrfResponse = await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, {
        withCredentials: true,
      });
      ////console.log('CSRF Response:', csrfResponse);

      const xsrfToken = getCookie('XSRF-TOKEN');
      ////console.log('XSRF Token:', xsrfToken);
      if (!xsrfToken) {
        throw new Error('CSRF token not found');
      }
      const response = form.id
        ? await axios.put(`${config.API_URL}/api/updatevotehead/${form.id}`, payload, {
            headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-XSRF-TOKEN': xsrfToken,
          },
          withCredentials: true,
          })
        : await axios.post(`${config.API_URL}/api/storevotehead`, payload, {
            headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-XSRF-TOKEN': xsrfToken,
          },
          withCredentials: true,
          });

      //console.log('Save votehead response:', response.data);
      toast.success(form.id ? 'Votehead updated' : 'Votehead added');
      fetchVoteheads();
      resetForm();
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        toast.error('Failed to save votehead');
      }
      console.error('Save votehead error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Delete votehead
  const deleteVotehead = async (id) => {
    //if (!confirm('Are you sure you want to delete this votehead?')) return;
    setLoading(true);
    try {
         const token = localStorage.getItem('token');
      //console.log('Votehead payload:', payload);
       ////console.log('Fetching CSRF cookie...');
      const csrfResponse = await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, {
        withCredentials: true,
      });
      ////console.log('CSRF Response:', csrfResponse);

      const xsrfToken = getCookie('XSRF-TOKEN');
      ////console.log('XSRF Token:', xsrfToken);
      if (!xsrfToken) {
        throw new Error('CSRF token not found');
      }
      await axios.delete(`${config.API_URL}/api/deletevotehead/${id}`, {
         headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-XSRF-TOKEN': xsrfToken,
          },
          withCredentials: true,
      });
      toast.success('Votehead deleted');
      fetchVoteheads();
    } catch (err) {
      toast.error('Failed to delete votehead');
      console.error('Delete votehead error:', err);
    } finally {
      setLoading(false);
    }
  };

 // Edit votehead
  const editVotehead = async (voteheadId) => {
    try {
      const response = await axios.get(`${config.API_URL}/api/editvotehead/${voteheadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      //console.log('Edit votehead API response:', response.data);

      const votehead = response.data.data;

      if (!votehead) {
        toast.error('Votehead not found');
        return;
      }

      setForm({
        id: votehead.id,
        name: votehead.name || '',
        price: votehead.price || '',
      });
      setShowModal(true);
    } catch (err) {
      toast.error('Failed to fetch votehead details');
      console.error('Fetch votehead details error:', err);
    }
  };

  // Reset form
  const resetForm = () => {
    setForm({
      id: '',
      name: null,
      price: '',
    });
    setShowModal(false);
    setErrors({});
  };

 useEffect(() => {
  if (!token) {
    toast.error('Please log in to access this page');
    console.error('No token found');
    return;
  }
  fetchVoteheads();
}, [mode]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white shadow-sm rounded-lg mb-6 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FileText className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{mode} - Voteheads</h1>
                <p className="text-sm text-gray-600">Manage voteheads and their amounts</p>
              </div>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add Votehead</span>
            </button>
          </div>
        </div>

        {/* Voteheads Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100">
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : voteheads.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Votehead</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Amount (KES)</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {voteheads.map(votehead => (
                      <tr key={votehead.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">{votehead.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          KES {Number(votehead.price || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <button
                            onClick={() => editVotehead(votehead.id)}
                            className="text-blue-600 hover:text-blue-800 mr-4"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deleteVotehead(votehead.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-600">No voteheads found</div>
            )}
          </div>
        </div>

        {/* Votehead Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {form.id ? 'Edit Votehead' : 'Add Votehead'}
                </h3>
                <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Votehead Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    className={`w-full px-3 py-2 border ${
                      errors.name ? 'border-red-300' : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  />
                  {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name[0]}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (KES)</label>
                  <input
                    type="number"
                    value={form.price}
                    onChange={e => setForm({ ...form, price: e.target.value })}
                    className={`w-full px-3 py-2 border ${
                      errors.price ? 'border-red-300' : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  />
                  {errors.price && <p className="text-xs text-red-600 mt-1">{errors.price[0]}</p>}
                </div>
              </div>
              <div className="flex space-x-4 mt-6">
                <button
                  onClick={resetForm}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={saveVotehead}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoteheadManagement;